import { CSSProperties, FC, useState } from 'react';
import { Avatar, Button, Divider, Popconfirm } from 'antd';
import { TbArrowRight, TbBuilding, TbX } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { VirtualizedList as List, SkeletonList } from '@/shared';
import { useInvitationStore } from '@/stores/database/aliases/invitations';
import { SetStateMethod } from '@/types';

import { EmptyJoinableWorkspaces } from '../../components';

interface JoinWorkspaceProps {
    height: CSSProperties['height'];
    setOpen: SetStateMethod<boolean>;
}

const JoinWorkspace: FC<JoinWorkspaceProps> = ({ height, setOpen }): JSX.Element => {
    const { invitations, deleteInvitation, loading, acceptInvitationById } = useInvitationStore();

    const [buttonLoading, setButtonLoading] = useState(false);

    const { token } = useAntDesignTheme();

    return (
        <div
            style={{
                border: `1px dashed ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                boxSizing: 'border-box',
                height,
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            {invitations?.length !== 0 && !loading ? (
                <List
                    style={{
                        height: '100%',
                        textAlign: 'start',
                        minHeight: 0,
                        maxHeight: '100%',
                        overflowY: 'auto',
                    }}
                    size='large'
                    itemLayout='horizontal'
                    dataSource={invitations}
                    renderItem={(invitation) => (
                        <List.Item
                            style={{ padding: '0.5rem 1rem', paddingRight: '0.5rem' }}
                            key={invitation?.id}
                            actions={[
                                <>
                                    <Button
                                        size='small'
                                        type='primary'
                                        disabled={buttonLoading}
                                        loading={buttonLoading}
                                        onClick={async () => {
                                            try {
                                                setButtonLoading(true);
                                                setOpen(false);
                                                await acceptInvitationById(invitation?.id);
                                            } catch {
                                                setOpen(true);
                                                toast.error('Unable to Join Workspace');
                                            } finally {
                                                setButtonLoading(false);
                                            }
                                        }}
                                    >
                                        Join
                                        <TbArrowRight size='1rem' style={{ verticalAlign: '-0.2rem' }} />
                                    </Button>
                                    <Divider type='vertical' />
                                    <Popconfirm
                                        placement='rightTop'
                                        overlayStyle={{ maxWidth: 16 * 20 }}
                                        title={
                                            'Are you sure you want to dismiss this invite? You will not be able to join this workspace unless you are re-invited by its owner or administrators.'
                                        }
                                        onConfirm={async () => {
                                            try {
                                                await deleteInvitation(invitation?.id);
                                            } catch {
                                                toast.error('Unable to Dismiss Invitation');
                                            }
                                        }}
                                        okText='Yes'
                                        cancelText='No'
                                    >
                                        <Button
                                            size='small'
                                            disabled={loading}
                                            style={{ marginRight: 0 }}
                                            icon={
                                                <TbX
                                                    size='1rem'
                                                    style={{
                                                        verticalAlign: '-0.175rem',
                                                        color: token.colorTextTertiary,
                                                    }}
                                                />
                                            }
                                            type='dashed'
                                        />
                                    </Popconfirm>
                                </>,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={
                                    <div style={{ marginTop: '0.25rem' }}>
                                        <Avatar
                                            shape='square'
                                            size='large'
                                            icon={
                                                invitation?.workspace?.emoji ? (
                                                    invitation?.workspace?.emoji
                                                ) : (
                                                    <TbBuilding
                                                        style={{
                                                            verticalAlign: '-0.2rem',
                                                            color: token.colorTextTertiary,
                                                        }}
                                                    />
                                                )
                                            }
                                            style={{ border: `1px solid ${token.colorBorder}` }}
                                        />
                                    </div>
                                }
                                title={
                                    <>
                                        {invitation?.workspace?.name}
                                        {/* !IMPORTANT: I think the concept of a "Verified Organization" is amazing, but we should handle it by: */}
                                        {/* - Verified Domain + Workspace Owner has Account Verified */}

                                        {/* {(invitation?.workspace?.type === WorkspaceType.ORGANIZATION ||
                                            invitation?.workspace?.type === WorkspaceType.ENTERPRISE) && (
                                            <SmallTooltip
                                                title={
                                                    <>
                                                        This workspace has been verified by its workspace owner. Learn
                                                        more in{' '}
                                                        <a
                                                            href='https://docs.countercyclical.io/fundamentals/settings/workspace/permissions#verified-domains'
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                        >
                                                            our docs
                                                        </a>
                                                    </>
                                                }
                                                placement='topLeft'
                                            >
                                                <Tag
                                                    style={{ marginLeft: '0.5rem', borderColor: 'transparent' }}
                                                    color='green'
                                                >
                                                    <TbRosetteDiscountCheckFilled
                                                        style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }}
                                                    />
                                                    Verified Organization
                                                </Tag>
                                            </SmallTooltip>
                                        )} */}
                                    </>
                                }
                                description={<>{invitation?.workspace?.type}</>}
                                style={{ paddingRight: 0 }}
                            />
                        </List.Item>
                    )}
                />
            ) : loading ? (
                <div style={{ boxSizing: 'border-box', height: '100%', padding: '1rem' }}>
                    <SkeletonList avatars={false} listLength={6} minHeight='100%' maxHeight='100%' />
                </div>
            ) : (
                <EmptyJoinableWorkspaces />
            )}
        </div>
    );
};

export default JoinWorkspace;
